import React, { useState } from 'react';
import { X, Star, HelpCircle, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { AIText, submitReview } from '../../../lib/api/restaurant.apis';

interface ReviewFormProps {
  showReviewForm: boolean;
  setShowReviewForm: (show: boolean) => void;
  restaurantId: string;
} 

const CircleSpinner = ({ size = 20, color = "#ff4e1d" }) => (
  <div
    className="inline-block animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
    style={{ width: size, height: size, borderColor: `${color} transparent ${color} ${color}` }}
    role="status"
  />
);

const ReviewForm: React.FC<ReviewFormProps> = ({ showReviewForm, setShowReviewForm, restaurantId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    companions: [],
    comment: '',
    name: '',
  });

  // Helper wizard state
  const [showHelpWizard, setShowHelpWizard] = useState(false);
  const [helpStep, setHelpStep] = useState(1);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [helpAnswers, setHelpAnswers] = useState({
    enjoyed: '',
    improve: '',
    recommend: '',
  });

  const companionOptions = ['Solo', 'Partner', 'Family', 'Friends', 'Business'];
  const reviewAspects = ['Atmosphere', 'Service', 'Value', 'Cleanliness', 'Location'];

  const handleCompanionToggle = (companion) => {
    setFormData(prev => ({
      ...prev,
      companions: prev.companions.includes(companion)
        ? prev.companions.filter(c => c !== companion)
        : [...prev.companions, companion]
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simulate submission
    try {
        await submitReview(restaurantId, formData);
        setShowReviewForm(false);
    } catch (error) {
        console.error(error);
        alert("failed to submit, check your connection");
    } finally {
        setIsSubmitting(false);
    }
  };

  // Helper wizard handlers
  const openHelpWizard = () => {
    setShowHelpWizard(true);
    setHelpStep(1);
    setHelpAnswers({ enjoyed: '', improve: '', recommend: '' });
  };

  const closeHelpWizard = () => {
    setShowHelpWizard(false);
    setHelpStep(1);
  };

  const goToNextStep = () => {
    if (helpStep < 3) {
      setHelpStep(helpStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (helpStep > 1) {
      setHelpStep(helpStep - 1);
    }
  };

  const updateHelpAnswer = (field, value) => {
    setHelpAnswers(prev => ({ ...prev, [field]: value }));
  };

  const generateReviewFromAI = async () => {
    setIsGeneratingAI(true);

    try {
      const response = await AIText({
        type: "review",
        enjoyed: helpAnswers.enjoyed,
        improve: helpAnswers.improve,
        recommend: helpAnswers.recommend,
      });

      setFormData(prev => ({ ...prev, comment: response.text || "" }));

      setShowHelpWizard(false);
      setHelpStep(1);
    } catch (error) {
      console.error("Error generating review:", error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const getCurrentStepContent = () => {
    switch (helpStep) {
      case 1:
        return {
          title: "What did you like the most?",
          placeholder: "Was it the friendly staff? The cozy atmosphere? The delicious food? Tell us what made you smile...",
          value: helpAnswers.enjoyed,
          field: 'enjoyed'
        };
      case 2:
        return {
          title: "Anything that could have been better?",
          placeholder: "Don't worry, honest feedback helps! Maybe the wait time, the price, or something small that could improve...",
          value: helpAnswers.improve,
          field: 'improve'
        };
      case 3:
        return {
          title: "Would you tell your friends about this place?",
          placeholder: "Would you bring your family here? Recommend it to a coworker? Why or why not?...",
          value: helpAnswers.recommend,
          field: 'recommend'
        };
      default:
        return null;
    }
  };

  const isCurrentStepValid = () => {
    const content = getCurrentStepContent();
    return content && content.value.trim().length > 0;
  };

  const currentStep = getCurrentStepContent();

  return (
    <>
      {showReviewForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 lg:p-8">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Write a Review</h3>
              <button
                onClick={() => setShowReviewForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
                disabled={isSubmitting}
              >
                <X className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              <div className="space-y-6">
                {/* Rating Section */}
                <div>
                  <h4 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">
                    How would you rate your experience?
                  </h4>
                  <div className="flex gap-2 lg:gap-4 justify-center mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="flex flex-col items-center gap-1 focus:outline-none group"
                        disabled={isSubmitting}
                      >
                        <div className="text-xs text-gray-500 group-hover:text-gray-700">
                          {star}.0
                        </div>
                        <Star
                          className={`w-10 h-10 lg:w-12 lg:h-12 ${
                            star <= formData.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          } ${isSubmitting ? 'opacity-50' : ''}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Companions Section */}
                <div>
                  <h4 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">
                    Who did you go with?
                  </h4>
                  <div className="flex flex-wrap gap-2 lg:gap-3">
                    {companionOptions.map((companion) => (
                      <button
                        key={companion}
                        type="button"
                        onClick={() => handleCompanionToggle(companion)}
                        className={`px-3 lg:px-4 py-2 rounded-full border transition-colors text-sm lg:text-base ${
                          formData.companions?.includes(companion)
                            ? 'bg-[#ff4e1d] text-white border-[#ff4e1d]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                        } ${isSubmitting ? 'opacity-50' : ''}`}
                        disabled={isSubmitting}
                      >
                        {companion}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Review Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-base lg:text-lg font-semibold text-gray-900">
                      Write your review
                    </h4>
                  </div>

                  {/* Review Tips */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Review tips</p>
                    <div className="flex flex-wrap gap-2">
                      {reviewAspects.map((aspect) => (
                        <span
                          key={aspect}
                          className="px-2 lg:px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs lg:text-sm"
                        >
                          {aspect}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Review Text */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Share your experience…
                    </label>
                    <div className="relative">
                      <textarea
                        value={formData.comment}
                        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4e1d] focus:border-transparent resize-none"
                        rows={6}
                        placeholder="Tell us about your experience..."
                        disabled={isSubmitting}
                        maxLength={5000}
                      />
                      {!formData.comment && (
                        <button
                          type="button"
                          onClick={openHelpWizard}
                          className="absolute top-3 right-3 flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-[#ff4e1d] hover:bg-gray-50 hover:border-[#ff4e1d] transition-all text-sm font-medium shadow-sm"
                          disabled={isSubmitting}
                        >
                          <Sparkles className="w-4 h-4" />
                          Help me write
                        </button>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData?.comment?.length || 0}/50 min characters
                    </div>
                  </div>

                  {/* Name Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4e1d] focus:border-transparent"
                      placeholder="Enter your name"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 lg:p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !formData.name || !formData.comment || formData.comment.length < 50}
                  className="flex-1 px-6 py-3 bg-[#ff4e1d] text-white rounded-lg hover:bg-[#e53e1a] transition font-medium flex items-center gap-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <CircleSpinner size={16} color="#ffffff" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Review'
                  )}
                </button>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Wizard Overlay */}
      {showHelpWizard && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            {/* Wizard Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#ff4e1d]" />
                  <h3 className="text-lg font-semibold text-gray-900">Let's write your review together</h3>
                </div>
                <button
                  onClick={closeHelpWizard}
                  className="p-1 hover:bg-gray-100 rounded-full transition"
                  disabled={isGeneratingAI}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Step Indicator */}
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center flex-1">
                    <div
                      className={`h-2 rounded-full flex-1 transition-colors ${
                        step <= helpStep ? 'bg-[#ff4e1d]' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-500 mt-2">Step {helpStep} of 3</div>
            </div>

            {/* Wizard Content */}
            <div className="p-6">
              {currentStep && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">
                    {currentStep.title}
                  </h4>
                  <textarea
                    value={currentStep.value}
                    onChange={(e) => updateHelpAnswer(currentStep.field, e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff4e1d] focus:border-transparent resize-none"
                    rows={6}
                    placeholder={currentStep.placeholder}
                    disabled={isGeneratingAI}
                  />
                </div>
              )}
            </div>

            {/* Wizard Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={helpStep === 1 ? closeHelpWizard : goToPreviousStep}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition font-medium flex items-center gap-2"
                  disabled={isGeneratingAI}
                >
                  {helpStep === 1 ? (
                    'Cancel'
                  ) : (
                    <>
                      <ChevronLeft className="w-4 h-4" />
                      Back
                    </>
                  )}
                </button>

                {helpStep < 3 ? (
                  <button
                    onClick={goToNextStep}
                    disabled={!isCurrentStepValid()}
                    className="px-6 py-2 bg-[#ff4e1d] text-white rounded-lg hover:bg-[#e53e1a] transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={generateReviewFromAI}
                    disabled={!isCurrentStepValid() || isGeneratingAI}
                    className="px-6 py-2 bg-[#ff4e1d] text-white rounded-lg hover:bg-[#e53e1a] transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingAI ? (
                      <>
                        <CircleSpinner size={16} color="#ffffff" />
                        Writing your review...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Create my review
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
export default ReviewForm